import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import type React from "react";
import type { Ref } from "react";

import { Error as ErrorComponent } from "@krea/shared/design-system/fields/Error";
import { cn } from "@krea/shared/utils";

type CheckboxProps = Omit<
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
  "onCheckedChange" | "onChange"
> & {
  label?: React.ReactNode;
  labelClassName?: string;
  error?: string;
  labelUnbolded?: string;
  onChange?: (checked: boolean) => void;
  /** Classes for the box itself — `className` styles the wrapper. Needed to
   *  recolour it: the checked state is primary by default. */
  checkboxClassName?: string;
};

const CheckBox = ({
  className,
  disabled,
  tabIndex,
  error,
  checked,
  onKeyDown,
  onChange,
  id,
  labelUnbolded,
  label,
  labelClassName,
  checkboxClassName,
  ref,
  ...props
}: CheckboxProps & {
  ref?: Ref<React.ElementRef<typeof CheckboxPrimitive.Root>>;
}) => {
  return (
    <div className={cn("", className)}>
      <label
        htmlFor={id}
        className={cn(
          "flex cursor-pointer items-center text-sm",
          { "cursor-not-allowed opacity-50": disabled },
          labelClassName,
        )}
      >
        <CheckboxPrimitive.Root
          data-test-id="consent-checkbox"
          ref={ref}
          className={cn(
            "peer cursor-pointer mr-2 h-[19px] w-[19px] shrink-0 rounded-xs border-2 border-gray-500 data-[state=checked]:border-primary shadow-sm focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-white",
            { "border-red-500": error },
            { "bg-white": !checked },
            checkboxClassName,
          )}
          checked={checked}
          id={id}
          disabled={disabled}
          tabIndex={tabIndex}
          onKeyDown={onKeyDown}
          onCheckedChange={(checked) => onChange?.(checked === true)}
          {...props}
        >
          <CheckboxPrimitive.Indicator
            className={cn("flex items-center justify-center text-current")}
          >
            <Check strokeWidth={4} className="ml-px h-[16px] w-[16px]" />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        {label && <span>{label}</span>}

        {labelUnbolded && <span className="font-normal">{labelUnbolded}</span>}
      </label>
      {error && (
        <ErrorComponent className="ml-[26px] mt-1">{error}</ErrorComponent>
      )}
    </div>
  );
};
CheckBox.displayName = CheckboxPrimitive.Root.displayName;

// eslint-disable-next-line import/no-default-export
export default CheckBox;
